import {
  DefaultTransformer,
  deserialize,
  DocumentObject,
  serialize,
  Transformer,
  transform,
} from '@jsonapiworld/jsonapi-serializer';
import { backendApiGateway } from '~/client/backend';
import {
  Account,
  AccountCreateRequestPayload,
  AccountFilters,
  AccountImportConfig,
  AccountImportTemplate,
  AccountImportTransactionsPayload,
  AccountMeta,
  AccountResourceType,
  AccountStatement,
  AccountUpdate,
  AccountsFilters,
  AccountWithConsent,
  BalanceData,
} from '~/types/account';
import { Company } from '~/types/company';
import { InstitutionSource } from '~/types/institution';
import { MetaDataCursorPage, pagedMetaSchema } from '~/types/jsonapi';
import { Money } from '~/types/money';
import { isDefined } from '~/types/typeGuards';
import {
  buildJsonAPIQueryParams,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
} from '~/utils/jsonapi-query';

export const fetchAllAccounts = async (
  companyId: Company['id'],
  filters?: AccountsFilters,
): Promise<AccountWithConsent[]> => {
  let allAccounts: AccountWithConsent[] = [];
  let fetchedAccounts: AccountWithConsent[] = [];

  let cursor: MetaDataCursorPage['cursor'] | null = null;
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);

  if (isDefined(filters?.ids)) {
    filterBuilder.in('id', filters.ids);
  }
  if (filters?.consentStatuses?.length) {
    filterBuilder.in('consent.status', filters.consentStatuses);
  }

  if (filters?.manual === true) {
    filterBuilder.eq('source', InstitutionSource.USER);
  } else if (filters?.manual === false) {
    filterBuilder.neq('source', InstitutionSource.USER);
  }

  const includeBuilder = jsonAPIIncludeBuilder([
    'consent.institution',
    'bookkeepingAccount',
    'manualAccount',
    'linkedBankAccounts',
  ]);
  do {
    const pageBuilder = jsonAPICursorPaginatorBuilder({ size: 100 });
    if (cursor) pageBuilder.cursor(cursor);
    const searchParams = buildJsonAPIQueryParams(filterBuilder, includeBuilder, pageBuilder);

    const { data: results } = await backendApiGateway.get<DocumentObject & { meta: AccountMeta }>('/api/v1/accounts', {
      params: searchParams,
    });
    fetchedAccounts = deserialize<AccountWithConsent>(results) as AccountWithConsent[];
    allAccounts = allAccounts.concat(fetchedAccounts);
    const { page } = pagedMetaSchema.parse(results.meta);
    cursor = page.cursor;
  } while (fetchedAccounts.length === 100);

  return allAccounts;
};

export const fetchBalances = async (filters: AccountFilters) => {
  const filterBuilder = jsonAPIFilterBuilder();
  if (filters.companyId) filterBuilder.eq('company.id', filters.companyId);
  if (filters.idsIn) filterBuilder.in('id', filters.idsIn);
  if (isDefined(filters.ignoreBalance)) filterBuilder.eq('ignoreBalance', filters.ignoreBalance);
  filterBuilder.neq('consent.status', 'DISABLED');

  const searchParams = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<{
    balances_converted: BalanceData;
    balances: BalanceData[];
  }>('/api/v1/accounts/metadata', { params: searchParams });

  return { raw: data.balances, converted: data.balances_converted };
};

export const exportXmlAccounts = async (companyId: Company['id']) => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);

  const includeBuilder = jsonAPIIncludeBuilder(['consent.institution']);
  const pageBuilder = jsonAPICursorPaginatorBuilder({ size: 100 });

  const searchParams = buildJsonAPIQueryParams(filterBuilder, includeBuilder, pageBuilder);

  return await backendApiGateway.get<Blob>('/api/v1/accounts', {
    headers: {
      accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    responseType: 'blob',
    params: searchParams,
  });
};

export const fetchAccountBankDetailsUrl = async (accountId: Account['id']): Promise<string> => {
  const { data } = await backendApiGateway.get<{ url: string }>(`/api/v1/accounts/${accountId}/bank-details`, {
    headers: {
      Accept: 'application/json',
    },
  });
  return data.url;
};

export const fetchAccountStatements = async (accountId: Account['id']): Promise<AccountStatement[]> => {
  const { data } = await backendApiGateway.get<{ statements: AccountStatement[] }>(
    `/api/v1/accounts/${accountId}/statements`,
    {
      headers: {
        Accept: 'application/json',
      },
    },
  );
  return data.statements;
};

export const createAccount = async (entity: AccountCreateRequestPayload) => {
  const hasLinkedBankAccounts = isDefined(entity.linkedBankAccountIds);
  const payload = serialize(
    {
      nickname: entity.nickname,
      balance: entity.balance,
      availableBalance: entity.balance,
      currentBalance: entity.balance,
      currency: entity.balance.currency,
      identifiers: [],
      hidden: false,
      company: {
        id: entity.company.id,
      },
      consent: {
        id: entity.consent.id,
      },
      ...(hasLinkedBankAccounts && {
        linkedBankAccounts: entity.linkedBankAccountIds!.map((id) => ({ id })),
      }),
    },
    'account',
    {
      relationships: ['company', 'consent', ...(hasLinkedBankAccounts ? ['linkedBankAccounts'] : [])],
    },
  );
  const { data } = await backendApiGateway.post<void>('api/v1/accounts', payload);

  return data;
};

interface UpdateAccountPayload {
  id: string;
  ignoreBalance?: boolean;
  nickname?: string;
  hiddenAt?: Date | null;
  balance?: Money;
  currentBalance?: Money;
  availableBalance?: Money;
  bookkeepingAccount?: { id: string } | null;
  linkedBankAccounts?: { id: string }[];
}

class UpdateAccountTransformer extends Transformer<UpdateAccountPayload, {}> {
  constructor({
    hasBookkeepingAccount,
    hasLinkedBankAccounts,
  }: {
    hasBookkeepingAccount: boolean;
    hasLinkedBankAccounts: boolean;
  }) {
    super();
    this.type = AccountResourceType;
    this.relationships = {};
    if (hasBookkeepingAccount) {
      this.relationships.bookkeepingAccount = (payload: UpdateAccountPayload) =>
        transform()
          .withInput(payload.bookkeepingAccount)
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .withIncluded(false)
          .toContext();
    }
    if (hasLinkedBankAccounts) {
      this.relationships.linkedBankAccounts = (payload: UpdateAccountPayload) =>
        transform()
          .withInput(payload.linkedBankAccounts ?? [])
          .withTransformer(new DefaultTransformer(AccountResourceType))
          .withIncluded(false)
          .toContext();
    }
  }

  transform(payload: UpdateAccountPayload) {
    const { bookkeepingAccount: _b, linkedBankAccounts: _l, ...rest } = payload;
    return rest;
  }
}

export const updateAccount = async (account: AccountUpdate) => {
  const payload: UpdateAccountPayload = {
    id: account.id,
    ignoreBalance: account.ignoreBalance,
    nickname: account.nickname,
    hiddenAt: account.hiddenAt,
    balance: account.balance,
    currentBalance: account.balance,
    availableBalance: account.balance,
    bookkeepingAccount:
      account.bookkeepingAccountId === undefined
        ? undefined
        : account.bookkeepingAccountId
          ? { id: account.bookkeepingAccountId }
          : null,
    linkedBankAccounts: account.linkedBankAccountIds?.map((id) => ({ id })),
  };

  const resourceData = transform<UpdateAccountPayload, {}>()
    .withInput(payload)
    .withTransformer(
      new UpdateAccountTransformer({
        hasBookkeepingAccount: account.bookkeepingAccountId !== undefined,
        hasLinkedBankAccounts: account.linkedBankAccountIds !== undefined,
      }),
    )
    .serialize();

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/accounts/${payload.id}`, resourceData);

  return deserialize<Account>(data) as Account;
};

export const deleteAccount = async (accountId: Account['id']) => {
  await backendApiGateway.delete<void>(`/api/v1/accounts/${accountId}`);
};

export const requestDeletionAccount = async (accountId: Account['id'], reason: string, more?: string) => {
  await backendApiGateway.post<void>(
    `/api/v1/accounts/${accountId}/request-deletion`,
    { reason, more },
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );
};

export const requestAcceptCashbackTermsAndCondition = async (accountId: Account['id']) => {
  await backendApiGateway.patch<void>(
    `/api/v1/accounts/${accountId}/accept-cashback-tnc`,
    {},
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );
};

export const fetchAccountImportTemplates = async (accountId: string) => {
  const { data } = await backendApiGateway.get<{ templates: AccountImportTemplate[] }>(
    `/api/v1/accounts/${accountId}/import-templates`,
  );
  return data.templates;
};

export const importTransactions = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: AccountImportTransactionsPayload;
}) => {
  await backendApiGateway.post(`/api/v1/accounts/${accountId}/import-transactions`, payload, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
};

export const fetchAccountImportConfig = async ({
  accountId,
  templateId,
}: {
  accountId: string;
  templateId: string;
}) => {
  const { data } = await backendApiGateway.get<{ config: AccountImportConfig }>(
    `/api/v1/accounts/${accountId}/import-config`,
    {
      params: {
        template: templateId,
      },
    },
  );
  return data.config;
};
